import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/api";
import { WhatsAppMessage } from "../types/api";

const PAGE_SIZE = 50;

export function useWhatsAppMessages(phoneNumber: string | undefined, token: string) {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ["whatsapp-messages", phoneNumber],
    queryFn: ({ pageParam = 1 }) =>
      apiService.getWhatsAppMessages({
        token,
        phone_number: phoneNumber!,
        page: pageParam as number,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.page + 1 : undefined,
    enabled: !!phoneNumber,
    staleTime: 1000 * 60, // 1 min
  });

  // Flatten pages into a chronological message list
  const messages: WhatsAppMessage[] = query.data?.pages.flatMap((p) => p.messages) ?? [];

  function addMessage(msg: WhatsAppMessage) {
    queryClient.setQueryData(
      ["whatsapp-messages", phoneNumber],
      (old: typeof query.data) => {
        if (!old) return old;
        let pages = old.pages;
        // If this is a real outbound message (positive id), remove any optimistic
        // placeholder (negative id) with the same body added while waiting for the echo.
        if (msg.id > 0 && msg.direction === "outbound") {
          const recentCutoff = Date.now() - 30000;
          pages = pages.map((page) => ({
            ...page,
            messages: page.messages.filter(
              (m) =>
                !(m.id < 0 && m.direction === "outbound" &&
                  new Date(m.created_at).getTime() > recentCutoff)
            ),
          }));
        }
        // Deduplicate by real id
        const existingIds = new Set(pages.flatMap((p) => p.messages.map((m) => m.id)));
        if (existingIds.has(msg.id)) return { ...old, pages };
        const lastPage = pages[pages.length - 1];
        return {
          ...old,
          pages: [
            ...pages.slice(0, -1),
            { ...lastPage, messages: [...lastPage.messages, msg] },
          ],
        };
      }
    );
  }

  function addOptimisticMessage(body: string, phone: string, isTemplate?: boolean) {
    const optimistic: WhatsAppMessage = {
      id: -Date.now(),
      phone_number: phone,
      direction: "outbound",
      body,
      status: "sent",
      created_at: new Date().toISOString(),
      is_template: isTemplate,
    };
    queryClient.setQueryData(
      ["whatsapp-messages", phoneNumber],
      (old: typeof query.data) => {
        if (!old) return old;
        const lastPage = old.pages[old.pages.length - 1];
        return {
          ...old,
          pages: [
            ...old.pages.slice(0, -1),
            { ...lastPage, messages: [...lastPage.messages, optimistic] },
          ],
        };
      }
    );
  }

  function updateMessage(msg: WhatsAppMessage) {
    queryClient.setQueryData(
      ["whatsapp-messages", phoneNumber],
      (old: typeof query.data) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)),
          })),
        };
      }
    );
  }

  return {
    messages,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    addMessage,
    addOptimisticMessage,
    updateMessage,
  };
}
